import { getFocusableComponents } from 'helpers/element';
import { isNodeArgsInDocumentOrder } from 'helpers/node';
import { useEffect, useRef } from 'react';
import { defaultStrategy, STRATEGIES } from '../constants/keyboardTrap';
import { StrategyType } from '../types/keyboardTrap';
import { useModal } from '.';

export default (
    modalRef: React.RefObject<HTMLDivElement>,
    isOpen: boolean,
    strategy: StrategyType = defaultStrategy,
) => {
    const { setActiveModalId } = useModal();
    const trapContainerElementRef = useRef<HTMLElement | null>(null);
    const trapTriggerElementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const handleLockStrategy = (event: KeyboardEvent) => {
            const isForwardTab = !event.shiftKey;
            const focusableElements = getFocusableComponents(modalRef.current);
            const { activeElement } = document;
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];

            const isActiveAfterLastFocusable = isNodeArgsInDocumentOrder(lastFocusable, activeElement);
            const isActiveBeforeFirstFocusable = isNodeArgsInDocumentOrder(activeElement, firstFocusable);

            const isLastFocusableActive = activeElement === lastFocusable;
            const isFirstFocusableActive = activeElement === firstFocusable;

            if (isForwardTab && (isLastFocusableActive || isActiveAfterLastFocusable)) {
                event.preventDefault();
                firstFocusable.focus();
            } else if (!isForwardTab && (isFirstFocusableActive || isActiveBeforeFirstFocusable)) {
                event.preventDefault();
                lastFocusable.focus();
            }
        };

        const handleWalkthroughStrategy = (event: KeyboardEvent) => {
            const isForwardTab = !event.shiftKey;

            if (trapContainerElementRef.current) {
                const focusableElements = getFocusableComponents(trapContainerElementRef.current);
                event.preventDefault();
                const { activeElement } = document;

                const focusedElementIndex = (() => {
                    let tmpIndex = -1;

                    focusableElements.forEach((item, index) => {
                        if (item === activeElement) {
                            tmpIndex = index;
                        }
                    });

                    return tmpIndex;
                })();
                const elementToFocus = isForwardTab
                    ? focusableElements[focusedElementIndex + 1]
                    : focusableElements[focusedElementIndex - 1];

                if (elementToFocus) {
                    event.preventDefault();
                    elementToFocus.focus();
                } else if (isForwardTab) {
                    event.preventDefault();
                    trapTriggerElementRef.current?.focus();
                    trapTriggerElementRef.current = null;
                    trapContainerElementRef.current = null;
                    setActiveModalId(null);
                } else if (!isForwardTab) {
                    event.preventDefault();
                    trapTriggerElementRef.current?.focus();
                    trapTriggerElementRef.current = null;
                    trapContainerElementRef.current = null;
                    setActiveModalId(null);
                }
            } else {
                const ariaControlsValue = (event.target as Element).getAttribute('aria-controls');

                if (ariaControlsValue && isForwardTab) {
                    trapTriggerElementRef.current = event.target as HTMLElement;
                    trapContainerElementRef.current = document.getElementById(ariaControlsValue);
                    const focusableElements = getFocusableComponents(trapContainerElementRef.current);

                    event.preventDefault();
                    focusableElements[0].focus();
                }
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (modalRef.current !== null && isOpen && ['Tab', 'Escape'].includes(event.code)) {
                if (strategy === defaultStrategy) {
                    handleLockStrategy(event);
                } else if (strategy === STRATEGIES.walkthrough) {
                    handleWalkthroughStrategy(event);
                }
            }
        };

        if (isOpen && modalRef.current && strategy === defaultStrategy) {
            const focusableElements = getFocusableComponents(modalRef.current);

            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        }

        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, modalRef, strategy, setActiveModalId]);
};
